import { useEffect, useState, type FormEvent } from "react"
import { AlertTriangle, Lock, Search } from "lucide-react"
import { authenticateGuestByBooking } from "../api"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

interface Props {
  open: boolean
  slug: string
  authMode: "pms" | "ical"
  requiresPinForSensitive?: boolean
  onClose: () => void
  onSuccess: (guestToken: string) => void | Promise<void>
}

export function ReserveUnlockDialog({ open, slug, authMode, onClose, onSuccess }: Props) {
  const [lastName, setLastName] = useState("")
  const [checkIn, setCheckIn] = useState("")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setLastName("")
    setCheckIn("")
    setBusy(false)
    setError(null)
  }, [open])

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (busy) return

    if (authMode !== "pms") {
      setError("This property is still using a legacy access flow. Please contact your host.")
      return
    }

    if (!lastName.trim() || !checkIn.trim()) {
      setError("Use the booking surname and check-in date to continue.")
      return
    }

    setBusy(true)
    setError(null)
    try {
      const result = await authenticateGuestByBooking(slug, lastName.trim(), checkIn.trim())
      await onSuccess(result.guest_token)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not verify those details.")
    } finally {
      setBusy(false)
    }
  }

  const unsupportedMode = authMode !== "pms"

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => {
      if (!nextOpen) onClose()
    }}>
      <DialogContent className="max-w-[min(100vw-1.5rem,28rem)] rounded-[1.75rem] border-border/70 bg-background/95 p-0 shadow-2xl backdrop-blur-xl sm:rounded-[2rem]">
        <div className="relative overflow-hidden rounded-[inherit] border border-white/40 bg-[linear-gradient(180deg,rgba(255,255,255,0.72),rgba(255,255,255,0.92))]">
          <div
            aria-hidden="true"
            className="absolute inset-x-0 top-0 h-28"
            style={{
              background:
                "radial-gradient(circle at top left, color-mix(in srgb, var(--sf-primary) 18%, transparent), transparent 50%), linear-gradient(180deg, rgba(255,255,255,0.32), transparent)",
            }}
          />
          <DialogHeader className="relative gap-3 px-6 pb-4 pt-6 text-left sm:px-7 sm:pt-7">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-border/60 bg-card/70 text-foreground shadow-sm backdrop-blur-sm">
              {unsupportedMode ? <AlertTriangle className="h-5 w-5" /> : <Lock className="h-5 w-5" />}
            </div>
            <div className="space-y-2">
              <div className="text-[0.7rem] font-semibold uppercase tracking-[0.24em] text-muted-foreground">
                Guest Access
              </div>
              <DialogTitle className="font-display text-[2rem] font-normal leading-tight text-foreground">
                Unlock stay details
              </DialogTitle>
              <DialogDescription className="max-w-[30ch] text-sm leading-6 text-muted-foreground">
                {unsupportedMode
                  ? "This link is using a legacy access flow that is no longer supported in this theme."
                  : "Confirm the booking surname and arrival date to reveal private access details."}
              </DialogDescription>
            </div>
          </DialogHeader>

          {unsupportedMode ? (
            <div className="relative px-6 pb-6 sm:px-7 sm:pb-7">
              <div className="rounded-[1.5rem] border border-destructive/20 bg-destructive/5 p-4 text-sm leading-6 text-foreground">
                Stayframe Reserve supports PMS verification only. Ask the host to resend a current guest link.
              </div>
              <div className="mt-4 flex justify-end">
                <Button type="button" variant="outline" onClick={onClose} className="rounded-full border-border/70 bg-card/80 px-5">
                  Close
                </Button>
              </div>
            </div>
          ) : (
            <form className="relative space-y-5 px-6 pb-6 sm:px-7 sm:pb-7" onSubmit={handleSubmit}>
              <div className="grid gap-4">
                <label className="grid gap-2 text-sm font-medium text-foreground">
                  Booking surname
                  <Input
                    autoComplete="family-name"
                    className="h-12 rounded-2xl border-border/70 bg-card/80 px-4 text-base shadow-sm focus-visible:ring-primary/40"
                    placeholder="Andersson"
                    value={lastName}
                    onChange={(event) => setLastName(event.target.value)}
                  />
                </label>
                <label className="grid gap-2 text-sm font-medium text-foreground">
                  Check-in date
                  <Input
                    type="date"
                    className="h-12 rounded-2xl border-border/70 bg-card/80 px-4 text-base shadow-sm focus-visible:ring-primary/40"
                    value={checkIn}
                    onChange={(event) => setCheckIn(event.target.value)}
                  />
                </label>
              </div>

              {error && (
                <div className="rounded-[1.25rem] border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-foreground">
                  {error}
                </div>
              )}

              <div className="flex items-center gap-3 rounded-[1.35rem] border border-border/60 bg-card/60 px-4 py-3 text-sm text-muted-foreground backdrop-blur-sm">
                <Search className="h-4 w-4 shrink-0 text-foreground" />
                Use the last name exactly as it appears on the booking.
              </div>

              <div className="flex items-center justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={busy}
                  className="rounded-full border-border/70 bg-card/80 px-5"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={busy}
                  className="rounded-full bg-primary px-5 text-primary-foreground hover:bg-primary/90"
                >
                  {busy ? "Verifying..." : "Unlock"}
                </Button>
              </div>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
